import { Box, Spinner } from '@chakra-ui/react'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { LuGrip } from 'react-icons/lu'
import TagComponent from '../shared/tag/TagComponent'
import { formatDataCompletenessPercentage, getDataCompletenessQualityColors } from './dataCompletenessIndicator.utils'

interface DataCompletenessTagProps {
  qualityPercentage: number | null | undefined
  hasItems: boolean
  icon?: ReactElement
  isLoading?: boolean
  labelMode?: 'full' | 'compact'
  size?: 'default' | 'small'
}

export const DataCompletenessTag = ({
  qualityPercentage,
  hasItems,
  icon,
  isLoading = false,
  labelMode = 'full',
  size = 'default',
}: DataCompletenessTagProps) => {
  const { t } = useTranslation()
  const colors = getDataCompletenessQualityColors(qualityPercentage, hasItems)
  const isSmall = size === 'small'
  const isCompact = labelMode === 'compact'

  const getTagText = () => {
    if (isLoading) return isCompact ? '' : t('components.data_completeness_indicator.data_quality_loading')
    if (!hasItems) return isCompact ? '–' : t('components.data_completeness_indicator.data_quality_empty')
    if (qualityPercentage == null) return isCompact ? 'n.v.' : t('components.data_completeness_indicator.data_quality_not_available')

    const formattedPercentage = formatDataCompletenessPercentage(qualityPercentage)
    if (isCompact) return `${formattedPercentage} %`

    return t('components.data_completeness_indicator.data_quality', {
      qualityPercentage: formattedPercentage,
    })
  }

  const text = getTagText()

  return (
    <Box>
      <TagComponent
        icon={
          <Box as='span' color={colors.textColor} display='inline-flex'>
            {icon ?? <LuGrip />}
          </Box>
        }
        borderWidth='1px'
        borderRadius='4px'
        text={text}
        rightIcon={isLoading ? <Spinner size='xs' color={colors.textColor} /> : undefined}
        color={{
          textColor: colors.textColor,
          background: colors.background,
          borderColor: colors.borderColor,
        }}
        fontWeight={100}
        fontSize={isSmall ? '12px' : undefined}
        height={isSmall ? '20px' : undefined}
      />
    </Box>
  )
}
